import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth, useAccess } from '../../context/AuthContext';
import Loader from '../../components/Loader';
import Badge from '../../components/Badge';
import { fetchDemoProgramme, provisionDemo } from '../../lib/demoProvisioning';

export default function DemoSettings() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const [demoProgramme, setDemoProgramme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!profile?.organisation_id) return;
    setLoading(true);
    try {
      const prog = await fetchDemoProgramme(profile.organisation_id);
      setDemoProgramme(prog);
    } catch (e) {
      console.error('[DemoSettings]', e);
      setToast({ msg: e.message || 'Failed to load demo status', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [profile?.organisation_id]);

  useEffect(() => { load(); }, [load]);

  async function handleRegenerate() {
    if (!window.confirm('Replace the current demo programme with a fresh copy from the template?')) return;
    setSaving(true);
    setToast(null);
    try {
      const result = await provisionDemo(getAccessTokenSilently, 'regenerate');
      setToast({ msg: 'Demo programme regenerated.', type: 'success' });
      await load();
      if (result?.programme_id) {
        navigate(`/programme/${result.programme_id}`);
      }
    } catch (e) {
      setToast({ msg: e.message || 'Regenerate failed', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="content">
        <p>Only organisation admins can manage demo content.</p>
      </div>
    );
  }

  if (loading) return <Loader text="Loading demo settings…" />;

  return (
    <div className="content">
      <div className="prog-hero">
        <div className="prog-hero-top">
          <div>
            <div className="prog-kicker">Administration</div>
            <div className="prog-title">Demo programme</div>
            <div className="prog-subtitle">
              Self-serve demo content cloned from the platform template — charter through ROM.
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className={toast.type === 'error' ? 'form-error' : 'auth-info'} role="status" style={{ marginBottom: 12 }}>
          {toast.msg}
        </div>
      )}

      <div className="card" style={{ padding: 20, maxWidth: 640 }}>
        <h3 style={{ marginTop: 0 }}>Current demo</h3>
        {demoProgramme ? (
          <>
            <p>
              <strong>{demoProgramme.name}</strong>{' '}
              <Badge cls="am">Demo</Badge>
            </p>
            <p style={{ color: '#666', fontSize: 14 }}>{demoProgramme.description}</p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/programme/${demoProgramme.id}`)}
            >
              Open demo programme
            </button>
          </>
        ) : (
          <p style={{ color: '#666' }}>No demo programme provisioned yet.</p>
        )}

        <hr style={{ margin: '20px 0' }} />

        <h3>Regenerate demo</h3>
        <p style={{ fontSize: 14, color: '#666' }}>
          Creates a fresh copy from the template (rate-limited to once per hour). Replaces any existing demo programme.
        </p>
        <button
          type="button"
          className="btn-primary"
          disabled={saving}
          onClick={handleRegenerate}
        >
          {saving ? 'Regenerating…' : demoProgramme ? 'Regenerate demo programme' : 'Create demo programme'}
        </button>
      </div>
    </div>
  );
}
